import React from 'react';
import imageCompression from 'browser-image-compression';

/**
 * Picking a file to send: validation against Meta's limits, a preview URL,
 * and light compression for large photos.
 *
 * Ported from the legacy `useFileAttachment` without its simulated upload —
 * the real one is in `useOutboundMedia`. Meta's limits are the ones the
 * connection layer enforces too (image 5 MB, audio/video 16 MB, document
 * 100 MB), so a refusal happens here, before any bytes travel.
 */
export const LIMITS = {
	image: 5 * 1024 * 1024,
	video: 16 * 1024 * 1024,
	audio: 16 * 1024 * 1024,
	document: 100 * 1024 * 1024,
};

/**
 * The formats Meta accepts natively for each kind.
 *
 * Kept as a list because it is what the interface must say when it refuses, and
 * because "WhatsApp does not accept that" is only useful if it can name what it
 * does accept.
 */
export const META_TYPES = {
	image: ['image/jpeg', 'image/png', 'image/webp'],
	video: ['video/mp4', 'video/3gpp'],
	audio: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'],
};

/**
 * Meta's message type for a browser file.
 *
 * ★ CLASSIFY BY WHAT IT IS, THEN CHECK WHETHER META TAKES IT.
 * This used to be an exact whitelist with a silent fallback to 'document', and
 * the fallback is what broke picture and video sending. A photo from an iPhone
 * is `image/heic`, a phone video is often `video/quicktime`, a screen capture
 * is `video/webm`, an animation is `image/gif` — none of them were on the
 * list, so each was declared to Meta as a DOCUMENT carrying an image or video
 * MIME type. Meta answered 400, the connection layer mapped it to 415, and the
 * agent read "WhatsApp does not accept that kind of file" about a perfectly
 * ordinary photo, with nothing naming the real problem.
 *
 * Classifying by prefix means the file is now declared as what it is. A format
 * Meta genuinely cannot take is still refused — but by `describeUnsupported`
 * below, which says which format and what to send instead.
 */
export function kindOf(file) {
	const t = String(file?.type ?? '').toLowerCase().split(';')[0].trim();
	if (t.startsWith('image/')) return 'image';
	if (t.startsWith('video/')) return 'video';
	if (t.startsWith('audio/')) return 'audio';
	return 'document';
}

/**
 * Why Meta will refuse this file, or null if it will not.
 *
 * Documents are deliberately unchecked: WhatsApp takes any document MIME type,
 * so the only limit that applies to them is size.
 */
export function describeUnsupported(file) {
	const kind = kindOf(file);
	if (kind === 'document') return null;

	const t = String(file?.type ?? '').toLowerCase().split(';')[0].trim();
	if (META_TYPES[kind].includes(t)) return null;

	const format = t.split('/')[1]?.toUpperCase() || 'that format';
	const accepted = META_TYPES[kind].map((m) => m.split('/')[1].toUpperCase()).join(', ');
	return `WhatsApp cannot send ${format} ${kind}s. Convert it to ${accepted} first, or send it as a document.`;
}

const mb = (bytes) => `${Math.round(bytes / 1024 / 1024)} MB`;

export function validate(file) {
	const kind = kindOf(file);

	// ★ FORMAT BEFORE SIZE, so a HEIC photo is told it is a HEIC photo rather
	// than being compressed and then refused by Meta for the real reason.
	const unsupported = describeUnsupported(file);
	if (unsupported) return { ok: false, kind, unsupported: true, reason: unsupported };

	const limit = LIMITS[kind];
	if (file.size > limit) {
		return { ok: false, kind, reason: `That ${kind} is over WhatsApp's ${mb(limit)} limit.` };
	}
	return { ok: true, kind };
}

export default function useAttachment() {
	const [attachment, setAttachment] = React.useState(null); // { file, kind, previewUrl }
	const [preparing, setPreparing] = React.useState(false);

	const clear = React.useCallback(() => {
		setAttachment((a) => {
			if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
			return null;
		});
	}, []);

	React.useEffect(() => () => clear(), [clear]);

	/** @returns {Promise<{ok:boolean, reason?:string}>} */
	const attach = React.useCallback(
		async (input) => {
			let file = input;
			const check = validate(file);
			// A photo over the limit is usually a phone camera original; shrink it
			// rather than refuse it. Anything else over the limit is refused.
			//
			// ⚠ `unsupported` is excluded: compressing a HEIC produces a smaller
			// HEIC, and Meta refuses it for the format either way. Re-encoding to
			// JPEG would silently change what the agent chose to send.
			if (!check.ok && !check.unsupported && check.kind === 'image') {
				try {
					setPreparing(true);
					file = await imageCompression(file, { maxSizeMB: 4.5, maxWidthOrHeight: 2048, useWebWorker: true });
				} catch (_) {
					setPreparing(false);
					return check;
				}
				setPreparing(false);
			} else if (!check.ok) {
				return check;
			}
			clear();
			setAttachment({ file, kind: kindOf(file), previewUrl: URL.createObjectURL(file) });
			return { ok: true };
		},
		[clear],
	);

	return { attachment, attach, clear, preparing };
}
